import { FC } from 'react'

interface SkeletonProps {
  className?: string
  width?: string | number
  height?: string | number
  rounded?: boolean
}

const Skeleton: FC<SkeletonProps> = ({ 
  className = '', 
  width = '100%', 
  height = '20px',
  rounded = false 
}) => {
  return (
    <div 
      className={`
        animate-pulse bg-gray-200 dark:bg-gray-700
        ${rounded ? 'rounded-full' : 'rounded'}
        ${className}
      `}
      style={{ width, height }}
    />
  )
}

// Stats Skeleton - for statistics cards
export const StatsSkeleton: FC = () => {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
      {[1, 2, 3, 4].map((i) => (
        <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between">
            <div className="space-y-2">
              <Skeleton width="60px" height="14px" />
              <Skeleton width="80px" height="24px" />
            </div>
            <Skeleton width="40px" height="40px" rounded />
          </div>
        </div>
      ))}
    </div>
  )
}

// List Skeleton - for list items
export const ListSkeleton: FC<{ items?: number }> = ({ items = 3 }) => {
  return (
    <div className="space-y-4">
      {Array.from({ length: items }).map((_, i) => (
        <div key={i} className="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div className="flex items-center space-x-4">
            <Skeleton width="48px" height="48px" rounded />
            <div className="flex-1 space-y-2">
              <Skeleton width="70%" height="16px" />
              <Skeleton width="40%" height="14px" />
            </div>
            <Skeleton width="60px" height="14px" />
          </div>
        </div>
      ))}
    </div>
  )
}

// Card Skeleton - for card layouts
export const CardSkeleton: FC = () => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
      <div className="space-y-4">
        <Skeleton width="100%" height="200px" />
        <div className="space-y-2">
          <Skeleton width="80%" height="20px" />
          <Skeleton width="60%" height="16px" />
          <Skeleton width="40%" height="14px" />
        </div>
      </div>
    </div>
  )
}

// Table Skeleton - for table layouts
export const TableSkeleton: FC<{ rows?: number; cols?: number }> = ({ 
  rows = 5, 
  cols = 4 
}) => {
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
      {/* Table Header */}
      <div className="border-b border-gray-200 dark:border-gray-700 p-4">
        <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
          {Array.from({ length: cols }).map((_, i) => (
            <Skeleton key={i} width="80px" height="16px" />
          ))}
        </div>
      </div>
      
      {/* Table Rows */}
      <div className="divide-y divide-gray-200 dark:divide-gray-700">
        {Array.from({ length: rows }).map((_, rowIndex) => (
          <div key={rowIndex} className="p-4">
            <div className="grid gap-4" style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }}>
              {Array.from({ length: cols }).map((_, colIndex) => (
                <Skeleton key={colIndex} width="100%" height="16px" />
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default Skeleton
